import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Switch } from '../ui/switch';
import { ShoppingBag, Image as ImageIcon, AlertCircle, ChevronRight } from 'lucide-react';

const ListingsListLite = ({ listings = [], onToggleStock, loading = false }) => {
  const navigate = useNavigate();

  const getFirstImage = (listing) => {
    // Try various image sources
    if (listing.images?.length) {
      const img = listing.images[0];
      return typeof img === 'string' ? img : img.url || img.src;
    }
    if (listing.media?.length) {
      const media = listing.media[0];
      return typeof media === 'string' ? media : media.url || media.src;
    }
    if (listing.thumbnail) return listing.thumbnail;
    if (listing.image) return listing.image;
    return null;
  };

  const getPrice = (listing) => {
    // iter-220 bug 3 — Show the bumped + tax-baked price (what the
    // customer actually sees on Shopify) when it has been computed.
    // Falls back to raw price for listings that have never synced.
    return (
      listing.bumped_price ||
      listing.aniyor_listing_price ||
      listing.price ||
      listing.base_price ||
      listing.data?.price ||
      0
    );
  };

  const isInStock = (listing) => {
    if (listing.status === 'out_of_stock') return false;
    if (listing.is_in_stock === false) return false;
    if (listing.inventory !== undefined && listing.inventory <= 0) return false;
    if (listing.stock !== undefined && listing.stock <= 0) return false;
    return listing.status === 'active' || listing.status === 'published';
  };

  const getStatusLabel = (listing) => {
    if (listing.status === 'pending_approval' || listing.status === 'pending') return 'Pending';
    if (listing.status === 'rejected') return 'Rejected';
    if (listing.status === 'draft') return 'Draft';
    return null;
  };

  // Get correct edit route based on listing type
  const getEditRoute = (listing) => {
    const type = listing.listing_type || listing.type || 'product';
    const id = listing.id || listing._id;
    return `/edit-listing/${type}/${id}`;
  };

  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i.id || i.name || `${i.title || "i"}-${i}`} className="flex items-center gap-3 p-3 bg-white rounded-lg animate-pulse">
            <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
            <div className="w-10 h-5 bg-gray-200 rounded-full"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!listings.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-white">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <ShoppingBag className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-gray-900 font-medium text-base mb-1">No listings yet</p>
        <p className="text-gray-500 text-sm">Add your first listing to start selling</p>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="listings-list-lite">
      {listings.map((listing, index) => {
        const imageUrl = getFirstImage(listing);
        const inStock = isInStock(listing);
        const statusLabel = getStatusLabel(listing);
        
        return (
          <div 
            key={listing.id || listing._id}
            className={`flex items-center gap-3 p-3 active:bg-gray-50 transition-colors cursor-pointer ${
              index !== listings.length - 1 ? 'border-b border-gray-100' : ''
            }`}
            onClick={() => navigate(getEditRoute(listing))}
            data-testid={`listing-item-${listing.id || listing._id}`}
          >
            {/* Thumbnail */}
            <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
              {imageUrl ? (
                <img 
                  src={imageUrl} 
                  alt={listing.title || 'Listing'} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`w-full h-full flex items-center justify-center ${imageUrl ? 'hidden' : ''}`}>
                <ImageIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            {/* Title & Price */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate leading-tight">
                {listing.title || 'Untitled'}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-sm font-semibold text-gray-900">
                  ₹{getPrice(listing).toLocaleString('en-IN')}
                </span>
                {statusLabel && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    statusLabel === 'Pending' ? 'bg-amber-100 text-amber-700' :
                    statusLabel === 'Rejected' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {statusLabel}
                  </span>
                )}
              </div>
            </div>
            
            {/* Stock Toggle or Status Icon */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {!statusLabel ? (
                <>
                  <div className="flex flex-col items-end">
                    <span className={`text-[10px] font-medium ${inStock ? 'text-emerald-600' : 'text-red-500'}`}>
                      {inStock ? 'In Stock' : 'Out'}
                    </span>
                  </div>
                  <Switch
                    checked={inStock}
                    onCheckedChange={(checked) => {
                      onToggleStock?.(listing, checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="data-[state=checked]:bg-emerald-500 scale-90"
                    data-testid={`toggle-stock-${listing.id}`}
                  />
                </>
              ) : statusLabel === 'Pending' ? (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-300" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ListingsListLite;
